import {Button, IconButton} from '@wandb/components';
import React, {useEffect} from 'react';

import {useProjectPageQuery} from '../../../generated/graphql';
import {
  CREATE_REPORT_FLASHER_FEATURE_KEY,
  useOnboardingContext,
  USER_TOGGLED_TABLE_VIEW_FEATURE_KEY,
} from '../../../util/userContext';
import {PersistentFlasher} from '../../elements/PersistentFlasher';
import {CreateReportTooltipPrompt} from '../../Reports';

export const PanelBankCreateReportButton: React.FC<{
  disabled: boolean;
  entityName: string;
  isIconOnly: boolean;
  projectName: string;
  onClick(): void;
}> = ({disabled, entityName, isIconOnly, projectName, onClick}) => {
  const projectQuery = useProjectPageQuery({
    variables: {
      entityName,
      projectName,
    },
  });
  const runCount = projectQuery.data?.project?.totalRuns;

  const {onboardingState, setOnboardingState, loading} = useOnboardingContext(
    CREATE_REPORT_FLASHER_FEATURE_KEY
  );
  const {onboardingState: tableViewState, loading: tableViewLoading} =
    useOnboardingContext(USER_TOGGLED_TABLE_VIEW_FEATURE_KEY);

  // Show the flashing blue dot if there are more than 20 runs
  // and the user hasn't yet clicked this button to dismiss createReport onboarding
  // and the 'toggle table view' flasher isn't present.
  const tableViewToggled =
    !tableViewLoading && !!tableViewState.toggledTableView;
  const flasherEnabled =
    !loading &&
    !onboardingState.createReportFlasher &&
    tableViewToggled &&
    runCount != null &&
    runCount > 20;

  useEffect(() => {
    if (flasherEnabled) {
      window.analytics?.track('Flasher Viewed', {
        entityName,
        projectName,
        location: 'create report button',
      });
    }
  }, [flasherEnabled, entityName, projectName]);

  const onCreateReportClick = () => {
    onClick();
    if (flasherEnabled) {
      window.analytics?.track('Flasher Dismissed', {
        entityName,
        projectName,
        location: 'create report button',
      });
    }
    setOnboardingState({...onboardingState, createReportFlasher: true});
  };

  return (
    <PersistentFlasher
      visible={flasherEnabled}
      offsetX={-69}
      style={{zIndex: 99}}
      trigger={
        isIconOnly ? (
          <IconButton
            icon="add-to-report"
            variant="secondary"
            aria-label="New Report"
            onPress={onCreateReportClick}
            isDisabled={disabled}
            tooltip={{
              children: 'New report',
              placement: 'bottom',
            }}
          />
        ) : (
          <Button
            variant="secondary"
            startIcon="add-new"
            onPress={onCreateReportClick}
            isDisabled={disabled}>
            New report
          </Button>
        )
      }
      popupClassName={`create-report-prompt-tooltip`}
      popupPosition={`bottom right`}
      popupOffsetX={13}
      popupContent={
        <CreateReportTooltipPrompt
          location={`workspace`}
          entityName={entityName}
          projectName={projectName}
        />
      }
    />
  );
};
